import wrapperHOC from "@Utils/wrapperHOC";
import styles from "./PersonalInfoContainer.module.scss";

const EnglishPersonalInfo = () => (
  <>
    <div className={styles.headingContainer}>
      <h1 className={`${styles.title} is-4 has-text-centered`}>
        Third-Party Personal Information Sharing List
      </h1>
    </div>
    <br />
    <br />

    {/* Content */}
    <div className={styles.containerSmall}>
      <div className={styles.row}>
        <>
          <div className={styles.introSection}>
            <p>
              <span>
                To provide you with stable, secure, and feature-rich services, our App integrates
                certain third-party SDKs.
              </span>
            </p>
            <p>
              <span>
                We strictly control the use of your personal information by these SDKs, and{" "}
              </span>
              <strong>they are only activated after you provide explicit consent</strong>
              <span>.</span>
            </p>
            <p>
              <span>
                Below is the full list of third-party SDKs we integrate, their purpose, the type of
                personal information they may process, and links to their privacy policies.
              </span>
            </p>
          </div>

          <hr className={styles.sectionDivider} />

          <div className={styles.sdkSection}>
            <h2 id="analytics-attribution" className={styles.sectionTitle}>
              1. Analytics & Attribution
            </h2>

            <div className={styles.sdkItem}>
              <h4 className={styles.sdkName} id="analytics-attribution-amplitude">
                (a) <strong>Amplitude</strong> (<code>@amplitude/analytics-react-native</code>)
              </h4>
              <div className={styles.sdkDetailsZh}>
                <p>
                  <strong>Purpose:</strong> Usage behavior analysis, product improvement.
                </p>
                <p>
                  <strong>Provider:</strong> Amplitude Inc.
                </p>
                <p>
                  <strong>Collected Information:</strong> Device identifiers (GAID/AAID, Android ID,
                  IDFV), session data, usage events.
                </p>
                <p>
                  <strong>Privacy Policy:</strong>{" "}
                  <a href="https://amplitude.com/privacy" target="_blank" rel="noopener noreferrer">
                    https://amplitude.com/privacy
                  </a>
                </p>
              </div>
            </div>

            <div className={styles.sdkItem}>
              <h4 className={styles.sdkName} id="analytics-attribution-firebase-analytics">
                (b) <strong>Firebase Analytics</strong> (
                <code>@react-native-firebase/analytics</code>)
              </h4>
              <div className={styles.sdkDetailsZh}>
                <p>
                  <strong>Purpose:</strong> App usage statistics, feature optimization.
                </p>
                <p>
                  <strong>Provider:</strong> Google LLC
                </p>
                <p>
                  <strong>Collected Information:</strong> GAID/AAID, device identifiers, usage
                  events, crash logs.
                </p>
                <p>
                  <strong>Privacy Policy:</strong>{" "}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://policies.google.com/privacy
                  </a>
                </p>
              </div>
            </div>

            <div className={styles.sdkItem}>
              <h4 className={styles.sdkName} id="analytics-attribution-singular">
                (c) <strong>Singular</strong> (<code>singular-react-native</code>)
              </h4>
              <div className={styles.sdkDetailsZh}>
                <p>
                  <strong>Purpose:</strong> Advertising attribution, campaign measurement.
                </p>
                <p>
                  <strong>Provider:</strong> Singular Labs Inc.
                </p>
                <p>
                  <strong>Collected Information:</strong> Advertising ID (GAID/AAID), OAID
                  (Android), device info, event data.
                </p>
                <p>
                  <strong>Privacy Policy:</strong>{" "}
                  <a
                    href="https://www.singular.net/privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.singular.net/privacy-policy/
                  </a>
                </p>
              </div>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          <div className={styles.sdkSection} id="crash-reporting">
            <h2 className={styles.sectionTitle}>2. Crash Reporting & Performance Monitoring</h2>

            <div className={styles.sdkItem}>
              <h4 className={styles.sdkName} id="crash-reporting-sentry">
                (a) <strong>Sentry</strong> (<code>@sentry/react-native</code>)
              </h4>
              <div className={styles.sdkDetailsZh}>
                <p>
                  <strong>Purpose:</strong> App crash diagnostics and performance analysis.
                </p>
                <p>
                  <strong>Provider:</strong> Functional Software Inc. (Sentry)
                </p>
                <p>
                  <strong>Collected Information:</strong> Device info (model, OS version), crash
                  logs, error traces.
                </p>
                <p>
                  <strong>Privacy Policy:</strong>{" "}
                  <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">
                    https://sentry.io/privacy/
                  </a>
                </p>
              </div>
            </div>

            <div className={styles.sdkItem}>
              <h4 className={styles.sdkName} id="crash-reporting-datadog">
                (b) <strong>Datadog</strong> (<code>@datadog/mobile-react-native</code>)
              </h4>
              <div className={styles.sdkDetailsZh}>
                <p>
                  <strong>Purpose:</strong> App stability monitoring, navigation and performance
                  tracking.
                </p>
                <p>
                  <strong>Provider:</strong> Datadog, Inc.
                </p>
                <p>
                  <strong>Collected Information:</strong> Device context, performance metrics,
                  non-sensitive logs.
                </p>
                <p>
                  <strong>Privacy Policy:</strong>{" "}
                  <a
                    href="https://www.datadoghq.com/legal/privacy/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.datadoghq.com/legal/privacy/
                  </a>
                </p>
              </div>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          <div className={styles.sdkSection} id="advertising-identifiers">
            <h2 className={styles.sectionTitle}>3. Advertising Identifiers & Social SDKs</h2>

            <div className={styles.sdkItem}>
              <h4 className={styles.sdkName} id="advertising-identifiers-fbsdk">
                (a) <strong>Facebook SDK</strong> (<code>react-native-fbsdk-next</code>)
              </h4>
              <div className={styles.sdkDetailsZh}>
                <p>
                  <strong>Purpose:</strong> Social login, advertising attribution, analytics.
                </p>
                <p>
                  <strong>Provider:</strong> Meta Platforms, Inc.
                </p>
                <p>
                  <strong>Collected Information:</strong> GAID/AAID, device identifiers, usage data.
                </p>
                <p>
                  <strong>Privacy Policy:</strong>{" "}
                  <a
                    href="https://www.facebook.com/privacy/policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.facebook.com/privacy/policy
                  </a>
                </p>
              </div>
            </div>

            <div className={styles.sdkItem}>
              <h4 className={styles.sdkName} id="advertising-identifiers-idfaaaidsdk">
                (b) <strong>IDFA/AAID SDK</strong> (<code>@sparkfabrik/react-native-idfa-aaid</code>
                )
              </h4>
              <div className={styles.sdkDetailsZh}>
                <p>
                  <strong>Purpose:</strong> Obtain advertising identifier for attribution.
                </p>
                <p>
                  <strong>Provider:</strong> Open-source package.
                </p>
                <p>
                  <strong>Collected Information:</strong> Advertising ID (GAID/AAID).
                </p>
                <p>
                  <strong>Privacy Policy:</strong> Depends on system provider (Google / Apple).
                </p>
              </div>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          <div className={styles.sdkSection} id="device-info">
            <h2 className={styles.sectionTitle}>4. Device Information</h2>

            <div className={styles.sdkItem}>
              <h4 className={styles.sdkName}>
                (a) <strong>React Native Device Info</strong> (<code>react-native-device-info</code>
                )
              </h4>
              <div className={styles.sdkDetailsZh}>
                <p>
                  <strong>Purpose:</strong> Collect device model, OS version, environment details
                  for app optimization and attribution syncing.
                </p>
                <p>
                  <strong>Provider:</strong> Open-source library.
                </p>
                <p>
                  <strong>Collected Information:</strong> Android ID, device model, OS version,
                  GAID/AAID, OAID (after consent).
                </p>
                <p>
                  <strong>Privacy Policy:</strong>{" "}
                  <a
                    href="https://github.com/react-native-device-info/react-native-device-info"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://github.com/react-native-device-info/react-native-device-info
                  </a>
                </p>
              </div>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          <div className={styles.sdkSection}>
            <h2 className={styles.sectionTitle} id="sensitive-identifier">
              5. Sensitive Identifiers (Accessed by Some SDKs After Consent)
            </h2>

            <div className={styles.sdkDetailsZh}>
              <ul className={styles.listViewPolicies}>
                <li>
                  <strong>IMEI (International Mobile Equipment Identity)</strong> → Attribution,
                  fraud prevention.
                </li>
                <li>
                  <strong>OAID (Open Anonymous Device Identifier)</strong> → Advertising
                  attribution, analytics (China-specific).
                </li>
                <li>
                  <strong>IMSI (International Mobile Subscriber Identity)</strong> → Fraud
                  detection, telecom integrity.
                </li>
                <li>
                  <strong>MAC Address</strong> → Legacy analytics/attribution use.
                </li>
              </ul>
              <p>
                These identifiers are accessed only when strictly required by an SDK, and{" "}
                <strong>only after you agree to our Privacy Policy</strong>.
              </p>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          <div className={styles.sdkSection}>
            <h2 className={styles.sectionTitle} id="cross-border-data-transfer">
              6. Cross-Border Data Transfer
            </h2>

            <div className={styles.sdkDetailsZh}>
              <p>
                Some SDK providers are located outside of China (e.g., USA, Singapore, EU). When
                these SDKs collect personal information, your data may be transmitted to their
                servers overseas.
              </p>
              <p>
                We ensure that such transfers comply with applicable Chinese regulations, and only
                occur after you grant consent.
              </p>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          <div className={styles.sdkSection}>
            <h2 className={styles.sectionTitle} id="your-rights">
              7. Your Rights
            </h2>

            <div className={styles.sdkDetailsZh}>
              <ul className={styles.listViewPolicies}>
                <li>
                  You may decline consent during the first launch of the App, in which case none of
                  the above SDKs will initialize.
                </li>
                <li>
                  You may withdraw consent at any time in <strong>Settings &gt; Privacy</strong>,
                  after which the SDKs will be disabled.
                </li>
                <li>
                  For any questions, you can contact us at:{" "}
                  <a href="mailto:contact@amberstudent.com">contact@amberstudent.com</a>.
                </li>
              </ul>
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          <div className={styles.sdkSection}>
            <div className={styles.sdkDetailsZh}>
              <p>
                <strong>Important:</strong> This list is updated whenever we integrate or remove
                third-party SDKs. The latest version will always be available on this page.
              </p>
            </div>
          </div>
        </>
      </div>
    </div>
  </>
);

export default wrapperHOC(EnglishPersonalInfo, {
  componentName: "EnglishPersonalInfo",
  showForChina: true,
});
